import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Heading,
    Button,
    Caption,
    useSnackbar,
    Breadcrumb,
    BreadcrumbItem
} from '@livingdesign/react';
import ViewPortfolioStyles from './ViewPortfolio.styles';
import MuiDataTable from '../../../../components/Table/MuiDataTable/MuiDataTable';
import { Skeleton } from '@mui/material';
import { getErrorMessage, getSensitiveMatterPayload, renderTernary, useUserAssociatedRelation } from '../../../../helpers/utils';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { getService, postService } from '../../../../axios/axios';
import { COMPOSITE_SEARCH_SERVICE, ORGANIZATION_SERVICE } from '../../../../constants/baseURLs';
import Listing from '../../../Listing/Listing';
import { CS_IS_ACTIVE_FILTER, CS_SCOPES, CS_TYPEAHEAD_PAGINATION_QUERY, MATTER_DEFAULT_SORT_QUERY, PORTFOLIO_MATTER_DEFAULT_SORT_QUERY } from '../../../../constants/constants';
import { formCompositeSearchQuery, formCsFilterObject } from '../../../../helpers/csQueryUtils';
import { formSearchQuery, matterDataMappingforDetail } from '../../../../helpers/tableUtils';
import { Operations } from '../../../../axios/operations';
import { useSelector } from 'react-redux';

/**
 * Component to view portfolio details with DisplayValue integration
 * Includes hyperlinked fields and edit functionality for portfolio name
 */
const ViewPortfolio = () => {
    const classes = ViewPortfolioStyles();
    const history = useHistory();
    const { portfolioId } = useParams();
    const [data, setData] = useState({ matters: [] });
    const [loading, setLoading] = useState(false);
    const [afaData, setAfaData] = useState({})
    const { addSnack } = useSnackbar();
    const matterAccessibility = useSelector((state) => state?.accessibility?.applicationAccess?.matters?.allMatters);
    const relations = useUserAssociatedRelation(matterAccessibility);
    const orgIdentifier = useSelector((state) => state?.organization?.organizationDetailValues?.identifier);
    const userDetails = useSelector((state) => state?.user?.userDetail?.attributes);
    
    
    
    let defaultFilterCondition = `${formSearchQuery(
        "appliedToObjectId",
        Operations.EQ,
        portfolioId,
        'id'
      )}+${formSearchQuery(
        "organizationId",
        Operations.EQ,
        orgIdentifier,
        'id'
      )}`;

    useEffect(() => {
        getPortfolioMatterData();
        getAFAData();
    }, [portfolioId])


    const getAFAData = () => {
        setLoading(true);
        getService(
            ORGANIZATION_SERVICE, // Replace with actual service constant
            `/organizations/search/afas/v1?filter=${defaultFilterCondition}`
        )
            .then((response) => {
                const res = response.data?.afas || response.data?.nodeList || [];
                setAfaData(res)
            })
            .catch((error) => {
                addSnack({
                    message: getErrorMessage(error)
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const getPortfolioMatterData = () => {
        setLoading(true);
        const portfolioQuery = [];
        portfolioQuery.push({
            "key": 'id',
            "value": portfolioId,
            "operation": "match"
        })

        const defaultQuery = {
            "filters": [CS_IS_ACTIVE_FILTER, ...portfolioQuery],
            "operation": "AND",
            "properties": null
        }
        const query = formCompositeSearchQuery([CS_SCOPES.PORTFOLIO], {}, {}, PORTFOLIO_MATTER_DEFAULT_SORT_QUERY, CS_TYPEAHEAD_PAGINATION_QUERY, {}, defaultQuery, {});
        postService(
            COMPOSITE_SEARCH_SERVICE,
            `/composite-search/v1?returnCsv=false`,
            query
        )
            .then((response) => {
                setLoading(false);
                setData(response?.data?.portfolio?.data?.[0]);
                getLeadAttorneyParalegal(response?.data?.portfolio?.data?.[0]?.matters);
            }).catch((error) => {
                setLoading(false)
                addSnack({
                    message: getErrorMessage(error)
                })
            })
    }

    const getLeadAttorneyParalegal = (matters) => {
        const isActiveFilter = formCsFilterObject('isActive', 'match', true);
        const orgIdFilter = formCsFilterObject('organizations.id', 'match', orgIdentifier);
        const selectedMatterQuery = formCsFilterObject("id", "in", matters.map((item) => item.id))
        const defaultQuery = {
          "filters": [isActiveFilter, orgIdFilter, selectedMatterQuery],
          "operation": "AND",
          "properties": null
        }
    
        const userRelation = {
          "filters": relations,
          "operation": "AND",
          "properties": null
        }
    
        const sortQueryMatter = { ...MATTER_DEFAULT_SORT_QUERY };
        let sensitiveMatterQuery = getSensitiveMatterPayload(matterAccessibility?.viewAll, userDetails?.userUniqueId);
        const query = formCompositeSearchQuery([CS_SCOPES.MATTER], {}, {}, sortQueryMatter, {}, {}, defaultQuery, userRelation, sensitiveMatterQuery);
    
        postService(
          COMPOSITE_SEARCH_SERVICE,
          `/composite-search/v1?returnCsv=false&organizationId=${orgIdentifier}&options=restrictMode`,
          query
        )
            .then((response) => {
                const matterResult = matterDataMappingforDetail(response?.data?.matter);
                setData((prev) => ({
                    ...prev,
                    matters: prev.matters?.map((existingMatter) => {
                        const updatedMatter = matterResult?.matters?.find(matter => matter?.id === existingMatter?.id);
                        
                        return {
                            ...existingMatter,
                            ...(updatedMatter?.leadAttorney && { leadAttorney: updatedMatter.leadAttorney }),
                            ...(updatedMatter?.leadParalegal && { leadParalegal: updatedMatter.leadParalegal })
                        };
                    })
                }));
            })
            .catch((error) => {
                console.log(error);
            })
    }

    const loadFieldProps = {
        loader: loading,
        skeletonHeight: '30px',
        skeletonVariant: 'reactangle',
        skeletonLines: 1,
        node: <></>
    };

    const loadField = ({ loader, skeletonHeight, skeletonVariant, skeletonLines = 1, node }) => {
        return renderTernary(loader, <Skeleton height={skeletonHeight} variant={skeletonVariant} lines={skeletonLines} />, node)
    };
    const matterColumns = [
        {
            name: "matterNumber",
            label: "Matter Number",
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value, tableMeta, updateValue) => {
                    const matter = data.matters[tableMeta.rowIndex];
                    const matterId = matter.id; // Using matterNumber as matterId for navigation

                    return renderHyperlink(value, () => history.push(`/matter/${matterId}`), tableMeta.rowIndex);
                },
            },
        },
        {
            name: "matterName",
            label: "Matter Name",
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value, tableMeta, updateValue) => {
                    const matter = data.matters[tableMeta.rowIndex];
                    const matterId = matter.id; // Using matterNumber as matterId for navigation

                    return renderHyperlink(value, () => history.push(`/matter/${matterId}`), tableMeta.rowIndex);
                },
            },
        },
        {
            name: "leadAttorney",
            label: "Lead Attorney",
            options: {
                filter: true,
                sort: true,
            },
        },
        {
            name: "leadParalegal",
            label: "Lead Paralegal",
            options: {
                filter: true,
                sort: true,
            },
        },
    ];

    const matterOptions = {
        selectableRows: "none",
        rowsPerPage: 15,
        responsive: "standard",
        pagination: true,
        search: true,
        filter: true,
        viewColumns: true,
        print: false,
        download: false,
        elevation: 0,
        tableBodyHeight: "auto",
    };

    const renderHyperlink = (text, onClick, rowIndex) => (
        <a
            href="#"
            onClick={(e) => {
                e.preventDefault();
                onClick();
            }}
            className={classes.hyperlink}
            data-testid={`matter-number-link-${rowIndex}`}
        >
            {text}
        </a>
    );

    return (
        <div>
            <div className={classes.breadCrumbs}>
                <Breadcrumb>
                    <BreadcrumbItem onClick={() => history.push('/')} data-testid="breadcrumb-afa-portfolios">
                        Portfolios
                    </BreadcrumbItem>
                    <BreadcrumbItem isActive>
                        {loading ? 'Loading...' : data?.portfolioName || 'Portfolio Details'}
                    </BreadcrumbItem>
                </Breadcrumb>
            </div>

            <Listing
                id="PortfolioPageTitle"
                title={loading ?
                    <Skeleton height="12px" variant="rectangle" width="100px" /> :
                    <span data-testid="portfolio-title">{data?.portfolioName || 'Portfolio Details'}</span>
                }
                tag={false}
            >

                <div className={classes.gridLayout}>
                    <div className={classes.pageInfo}>
                        <div className={classes.label}>
                            <Caption as="span" size="small">
                                Portfolio Code
                            </Caption>
                        </div>
                        <div className={classes.pageInfoBold}>
                            {loadField({
                                ...loadFieldProps, node:
                                    <Heading
                                        as="h4"
                                        size="small"
                                        id={`portfolio-code`}
                                        data-testid="portfolio-code-heading"
                                    >
                                        {data?.portfolioCode || '-'}
                                    </Heading>
                            })}
                        </div>
                    </div>

                    <div className={classes.pageInfo}>
                        <div className={classes.label}>
                            <Caption as="span" size="small">
                                Portfolio Description
                            </Caption>
                        </div>
                        <div className={classes.pageInfoBold}>
                            {loadField({
                                ...loadFieldProps, node:
                                    <Heading
                                        as="h4"
                                        size="small"
                                        id={`portfolio-description`}
                                        data-testid="portfolio-description-heading"
                                    >
                                        {data?.description || '-'}
                                    </Heading>
                            })}
                        </div>
                    </div>

                    <div className={classes.pageInfo}>
                        <div className={classes.label}>
                            <Caption as="span" size="small">
                                AFA Name
                            </Caption>
                        </div>
                        <div className={classes.pageInfoBold}>
                            {loadField({
                                ...loadFieldProps, node:
                                    <Heading
                                        as="h4"
                                        size="small"
                                        id={`afa-name`}
                                        data-testid="afa-name-heading"
                                    >
                                        {afaData?.name || '-'}
                                    </Heading>
                            })}
                        </div>
                    </div>

                    <div className={classes.pageInfo}>
                        <div className={classes.label}>
                            <Caption as="span" size="small">
                                AFA Status
                            </Caption>
                        </div>
                        <div className={classes.pageInfoBold}>
                            {loadField({
                                ...loadFieldProps, node:
                                    <Heading
                                        as="h4"
                                        size="small"
                                        id={`afa-status`}
                                        data-testid="afa-status-heading"
                                    >
                                        {afaData?.status || '-'}
                                    </Heading>
                            })}
                        </div>
                    </div>

                    <div className={classes.pageInfo}>
                        <div className={classes.label}>
                            <Caption as="span" size="small">
                                AFA Family
                            </Caption>
                        </div>
                        <div className={classes.pageInfoBold}>
                            {loadField({
                                ...loadFieldProps, node:
                                    <Heading
                                        as="h4"
                                        size="small"
                                        id={`afa-family`}
                                        data-testid="afa-family-heading"
                                    >
                                        {afaData?.family || '-'}
                                    </Heading>
                            })}
                        </div>
                    </div>

                    <div className={classes.pageInfo}>
                        <div className={classes.label}>
                            <Caption as="span" size="small">
                                AFA Type
                            </Caption>
                        </div>
                        <div className={classes.pageInfoBold}>
                            {loadField({
                                ...loadFieldProps, node:
                                    <Heading
                                        as="h4"
                                        size="small"
                                        id={`afa-type`}
                                        data-testid="afa-type-heading"
                                    >
                                        {afaData?.type || '-'}
                                    </Heading>
                            })}
                        </div>
                    </div>
                </div>

                <div className={classes.nestedTable}>
                    <MuiDataTable
                        data={data?.matters || []}
                        columns={matterColumns}
                        customOptions={matterOptions}
                        disableExport={true}
                        disableAddNew={true}
                        hideAddNewButton={true}
                        loading={loading}
                        disableSearch={false}
                        disableFilter={true}
                        noResultsText={"No matters linked to this portfolio"}
                        customClassName="nested-table"
                    />
                </div>
            </Listing>
        </div>
    );
};

export default ViewPortfolio;
